<script lang="ts">
	import ComponentCover from "../common/ComponentCover.svelte";
	import {
		ProductIcon,
		HorizontalChart,
		LeftRight,
		InlineButton,
	} from "@hitit/new_ui/src";
	import { t, api } from "../../../stores";
	import { formatISO } from "date-fns";
	import { onDestroy } from "svelte";

	export let storeIds: string[] = [];
	export let range = {
		start: new Date(),
		end: new Date(),
	};
	type Range = { start: Date; end: Date };
	type ChartData = {
		name: string;
		count: number;
	};
	// Contstants

	const maximumItemLength = 14;
	const x = (d: ChartData) => d.count;
	const barHeight = (data: ChartData) => {
		const index = chartData.findIndex((v) => v === data);
		if (0 <= index && 2 >= index) {
			return 40;
		} else {
			return 30;
		}
	};
	const yAxisLabel = (data: ChartData) => data.name;
	const dummyData = new Array(maximumItemLength)
		.fill(null)
		.map((_, i) => i + 1)
		.sort((a, b) => b - a);
	// Variables
	let noDatas = false;
	let chartData: ChartData[] = [];
	let graph: HTMLDivElement;
	let chart: SVGSVGElement;
	let __resizeEvent__ = () => {};
	let presentPage = 0;
	let totalPages = 0;
	let loading = true;

	function getSalesItems(range: Range, storeId: string) {
		const { start, end } = range;
		return $api.getStoreSalesReportStoreItems({
			startTimeInTimeZone: formatISO(start),
			endTimeInTimeZone: formatISO(end),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "YEARLY",
			storeid: storeId,
		});
	}
	function pagenationHandler(addNumb: -1 | 1) {
		const addedPage = presentPage + addNumb;
		const isOver = addedPage + 1 > totalPages;
		const underZero = addedPage < 0;

		if (isOver || underZero) return;

		presentPage = addedPage;
	}

	// Do
	$: (async (range: { start: Date; end: Date }, storeIds: string[]) => {
		try {
			const map = new Map();
			const allRanking = storeIds.map((id) => getSalesItems(range, id));
			const promises = await Promise.all(allRanking);
			loading = true;
			for (const result of promises) {
				const reports = result.data.salesReportStoreItems;
				for (const report of reports) {
					const { count, itemname } = report;
					const key = `${itemname}`;
					const originValue = map.get(key) || 0;
					const newValue = originValue + count;
					map.set(key, newValue);
				}
			}
			const topSalesItems = [...map]
				.sort((a, b) => {
					const [, aValue] = a;
					const [, bValue] = b;

					return bValue - aValue;
				})

				.map((v) => {
					const [name, count] = v;
					return {
						name,
						count,
					};
				});

			if (topSalesItems.length === 0) {
				noDatas = true;
				chartData = dummyData.map((v) => {
					return {
						name: "",
						count: v,
					};
				});
			} else {
				presentPage = 0;
				totalPages = Math.ceil(topSalesItems.length / maximumItemLength);
				noDatas = false;
				chartData = topSalesItems;
			}
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	})(range, storeIds);

	$: if (graph && chartData) {
		if (chart) {
			chart.remove();
			window.removeEventListener("resize", __resizeEvent__);
		}
		const slicedStartIndex = presentPage * maximumItemLength;
		const slicedEndIndex = (presentPage + 1) * maximumItemLength;
		const slicedData = chartData.slice(slicedStartIndex, slicedEndIndex);
		const customIndexing = (data: ChartData) =>
			chartData.findIndex((d) => d === data);
		const maximumDataValue = Math.max(...chartData.map((v) => v.count));
		const option = {
			x,
			marginRight: 100,
			gap: 20,
			barHeight,
			yAxisLabel,
			customIndexing,
			maximumDataValue,
		};

		const { node, resizeEvent } = HorizontalChart(graph, slicedData, option);
		chart = node;
		__resizeEvent__ = resizeEvent;
	}

	onDestroy(() => {
		window.removeEventListener("resize", __resizeEvent__);
	});

</script>

<div class="container">
	<div class="titles">
		<span class="sub-title"> {$t('hub.dashboard.graph.Product_share')} </span>
		<h2 class="main-title">
			<span class="icon">
				<ProductIcon />
			</span>
			HOT ITEM!
		</h2>
	</div>
	<div class="graph" bind:this={graph} class:noDatas />
	{#if noDatas}
		<ComponentCover coverColor={'transparents'} alignCenter>
			<span class="description"> {$t('hub.dashboard.no_enough_data')} </span>
		</ComponentCover>
	{/if}
	{#if !noDatas && !loading}
		<div class="pagenation">
			<InlineButton on:click={() => pagenationHandler(-1)}>
				<span class="pagenation-button left">
					<LeftRight direction={'left'} fill={'white'} />
				</span>
			</InlineButton>
			<div>{presentPage + 1}/{totalPages}</div>
			<InlineButton on:click={() => pagenationHandler(1)}>
				<span class="pagenation-button right">
					<LeftRight direction={'right'} fill={'white'} />
				</span>
			</InlineButton>
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../../variables.scss";
	.container {
		width: 100%;
		border-radius: 5px;
		padding: 20px 0px 50px 0px;
		display: flex;
		flex-direction: column;
		background-color: #6d6f76;
		gap: 30px;
		position: relative;

		.pagenation {
			position: absolute;
			bottom: 24px;
			right: 10px;
			display: flex;
			gap: 30px;
			color: white;
			.pagenation-button {
				width: 10px;
				height: 19.738px;
			}
		}
		.description {
			font-size: 1.25rem;
			font-weight: 500;
			color: white;
		}

		.graph {
			width: 100%;
			flex-grow: 1;
			box-sizing: border-box;
			padding-right: 10px;

			:global(svg .yAxis-label) {
				font-size: 1rem;
				fill: #46474b;
				font-weight: 200;
			}

			:global(svg .yAxis-label.right) {
				fill: white;
			}

			@for $i from 0 to 3 {
				:global(svg .yAxis-label-#{$i}) {
					fill: white;
					font-weight: 500;
					font-size: 1.25rem;
				}
			}
			:global(svg .bar) {
				fill: #d8d9df;
			}
			:global(svg .bar-0) {
				fill: #3c68d6;
			}
			:global(svg .bar-1) {
				fill: #6591fa;
			}
			:global(svg .bar-2) {
				fill: #a5c1f9;
			}

			:global(.xAxis-label) {
				display: flex;
				width: 100%;
				align-items: center;
				word-break: break-all;
				justify-content: flex-end;
				height: 100%;
				font-size: 1rem;
				color: white;
			}
			@for $i from 0 to 3 {
				:global(svg .xAxis-label-#{$i}) {
					font-weight: 550;
				}
			}

			&.noDatas {
				:global(svg .bar) {
					fill: #46474b;
				}
				:global(.xAxis-label) {
					display: none;
				}
			}
		}
		.titles {
			display: flex;
			flex-direction: column;
			gap: 10px;
			padding: 0px 10px;

			.sub-title {
				font-size: 1.25rem;
				font-weight: 500;
				color: #f0f0f4;
			}
			.main-title {
				display: flex;
				align-items: center;
				gap: 10px;
				font-weight: 600;
				font-size: 1.875rem;
				color: white;
				/* Poppins */

				.icon {
					width: 26px;
					height: 26px;
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 30px 30px 50px 30px;

			.pagenation {
				right: 30px;
				bottom: 34.3px;
				font-size: 1.25rem;
				line-height: 1rem; //1px
			}
			.graph {
				padding-right: 0px;
				:global(svg .yAxis-label) {
					font-size: 1.25rem;
				}

				@for $i from 0 to 3 {
					:global(svg .yAxis-label-#{$i}) {
						font-size: 1.5rem;
					}
				}
				:global(.xAxis-label) {
					font-size: 1.25rem;
				}
			}
			.titles {
				gap: 20px;
				padding: 0px;
				.sub-title {
					font-size: 1.5rem;
				}
				.main-title {
					font-size: 2.1875rem;
					.icon {
						width: 30px;
						height: 30px;
					}
				}
			}
		}
	}

</style>
